import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { Shell } from "../components/Shell";
import { ApiError } from "../lib/api";

type Step = "credentials" | "totp";

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (totpCode?: string) => {
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(
        email.trim().toLowerCase(),
        password,
        totpCode,
      );
      if (result.kind === "totp_required") {
        setStep("totp");
        return;
      }
      navigate(result.user.must_change_password ? "/change-password" : "/", {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(
          totpCode
            ? "Código TOTP inválido. Verifica el reloj de tu app."
            : "Credenciales inválidas.",
        );
      } else if (err instanceof ApiError && err.status === 403) {
        setError("Cuenta inactiva. Contacta al owner de la plataforma.");
      } else {
        setError("No se pudo conectar al servidor.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitCredentials = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };

  const onSubmitTotp = (e: FormEvent) => {
    e.preventDefault();
    void submit(code);
  };

  return (
    <Shell showHeader={false}>
      <section className="auth-stage">
        <div className="card card-narrow" style={{ width: "100%" }}>
          <div className="card-inner">
            <div className="eyebrow">
              Panel · {step === "credentials" ? "Acceso" : "Verificación 2FA"}
            </div>
            <h1 style={{ fontSize: 30 }}>
              {step === "credentials" ? "MiPueblo" : "Código de tu app"}
            </h1>
            <p className="lede" style={{ marginBottom: 28 }}>
              {step === "credentials"
                ? "Plataforma de atención ciudadana multicanal para alcaldías colombianas. Ingresa con tu cuenta de super_admin."
                : "Esta cuenta tiene 2FA activado. Abre Google Authenticator / Authy / 1Password y escribe el código de 6 dígitos."}
            </p>

            {error && <div className="alert err">{error}</div>}

            {step === "credentials" ? (
              <form onSubmit={onSubmitCredentials} noValidate>
                <div className="form-row">
                  <label className="label" htmlFor="email">
                    Correo electrónico
                  </label>
                  <input
                    id="email"
                    className="input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@dominio.com"
                    autoFocus
                  />
                </div>

                <div className="form-row">
                  <label className="label" htmlFor="password">
                    Contraseña
                  </label>
                  <input
                    id="password"
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <button
                  className="cta"
                  type="submit"
                  disabled={submitting || !email || !password}
                >
                  <span className="cta-inner">
                    {submitting && <span className="spinner" aria-hidden />}
                    {submitting ? "Verificando…" : "Ingresar"}
                  </span>
                </button>
              </form>
            ) : (
              <form onSubmit={onSubmitTotp} noValidate>
                <div className="form-row">
                  <label className="label" htmlFor="code">
                    Código TOTP
                  </label>
                  <input
                    id="code"
                    className="input"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    required
                    maxLength={6}
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="123 456"
                    style={{
                      fontFamily: "var(--font-mono)",
                      letterSpacing: "0.4em",
                      textAlign: "center",
                      fontSize: 22,
                    }}
                    autoFocus
                  />
                  <p className="helper">
                    El código rota cada 30 segundos.{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setStep("credentials");
                        setCode("");
                        setError(null);
                      }}
                    >
                      Volver
                    </a>
                  </p>
                </div>

                <button
                  className="cta"
                  type="submit"
                  disabled={submitting || code.length !== 6}
                >
                  <span className="cta-inner">
                    {submitting && <span className="spinner" aria-hidden />}
                    {submitting ? "Verificando…" : "Confirmar e ingresar"}
                  </span>
                </button>
              </form>
            )}

            <p className="helper" style={{ marginTop: 18 }}>
              ¿Problemas para entrar? Contacta al owner de la plataforma.
            </p>
          </div>
        </div>
      </section>
    </Shell>
  );
}
